// Personal & team tasks / calendar: to-dos, meetings, reminders — list + month
// calendar, mark-done, assign to a teammate (handoff), link to a client, and
// hand off to Google Calendar / .ics (with reminders).
import React, { useState, useEffect, useMemo } from 'react';
import { loadTasks, saveTask, deleteTask, loadUsers } from './firebase';

const pad = (n) => String(n).padStart(2, '0');
const fmtISO = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
const TYPE = { task: { label: 'To-do', color: '#3d567a' }, meeting: { label: 'Meeting', color: '#9a7d3d' }, reminder: { label: 'Reminder', color: '#7a3d6e' } };
const meta = (t) => TYPE[t] || TYPE.task;

function gcalLink(t) {
  let dates;
  if (t.time) {
    const [h, m] = t.time.split(':').map(Number);
    const s = t.date.replace(/-/g, '') + 'T' + pad(h) + pad(m) + '00';
    const e = t.date.replace(/-/g, '') + 'T' + pad((h + 1) % 24) + pad(m) + '00';
    dates = s + '/' + e;
  } else {
    const d = new Date(t.date + 'T00:00:00'); d.setDate(d.getDate() + 1);
    dates = t.date.replace(/-/g, '') + '/' + fmtISO(d).replace(/-/g, '');
  }
  return 'https://calendar.google.com/calendar/render?action=TEMPLATE&text=' + encodeURIComponent(t.title) + '&dates=' + dates + '&details=' + encodeURIComponent(t.notes || '');
}
function icsFor(t) {
  const L = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Tlhakiso//Tasks//EN', 'BEGIN:VEVENT', 'UID:tlh-task-' + (t.id || t.date) + '@tlhakiso'];
  if (t.time) {
    const [h, m] = t.time.split(':').map(Number);
    L.push('DTSTART:' + t.date.replace(/-/g, '') + 'T' + pad(h) + pad(m) + '00', 'DTEND:' + t.date.replace(/-/g, '') + 'T' + pad((h + 1) % 24) + pad(m) + '00');
  } else {
    const d = new Date(t.date + 'T00:00:00'); d.setDate(d.getDate() + 1);
    L.push('DTSTART;VALUE=DATE:' + t.date.replace(/-/g, ''), 'DTEND;VALUE=DATE:' + fmtISO(d).replace(/-/g, ''));
  }
  L.push('SUMMARY:' + t.title, 'DESCRIPTION:' + String(t.notes || '').replace(/\n/g, '\\n'), 'BEGIN:VALARM', 'TRIGGER:-PT30M', 'ACTION:DISPLAY', 'DESCRIPTION:Reminder', 'END:VALARM', 'END:VEVENT', 'END:VCALENDAR');
  return L.join('\r\n');
}
function downloadIcs(t) {
  const blob = new Blob([icsFor(t)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = (t.title || 'event').replace(/[^\w]+/g, '_') + '.ics'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

const inp = { width: '100%', padding: '7px 9px', fontSize: 12, border: '1px solid #e0ddd8', borderRadius: 5, boxSizing: 'border-box', marginTop: 3 };
const btnP = { padding: '8px 14px', borderRadius: 6, border: 'none', background: '#1a1a2e', color: '#c8a24e', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const btnG = { padding: '8px 14px', borderRadius: 6, border: '1px solid #e0ddd8', background: '#fff', color: '#444', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const Field = ({ label, children }) => <div style={{ marginBottom: 8 }}><label style={{ fontSize: 11, color: '#7a7568', fontWeight: 600 }}>{label}</label>{children}</div>;

function TaskModal({ task, users, projects, me, busy, onSave, onClose, onDelete }) {
  const [d, setD] = useState({ title: '', type: 'task', date: fmtISO(new Date()), time: '', assignedTo: me, projectId: '', notes: '', status: 'open', ...task });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 18, width: 380, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>{task && task.id ? 'Edit' : 'New'} item</div>
        <Field label="Title"><input style={inp} value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} autoFocus /></Field>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}><Field label="Type"><select style={inp} value={d.type} onChange={(e) => setD({ ...d, type: e.target.value })}><option value="task">To-do</option><option value="meeting">Meeting</option><option value="reminder">Reminder</option></select></Field></div>
          <div style={{ flex: 1 }}><Field label="Date"><input style={inp} type="date" value={d.date} onChange={(e) => setD({ ...d, date: e.target.value })} /></Field></div>
          <div style={{ width: 96 }}><Field label="Time"><input style={inp} type="time" value={d.time} onChange={(e) => setD({ ...d, time: e.target.value })} /></Field></div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}><Field label="Assign to">
            <input style={inp} list="tlh-users" value={d.assignedTo} onChange={(e) => setD({ ...d, assignedTo: e.target.value })} placeholder="email" />
            <datalist id="tlh-users">{(users || []).map((u) => <option key={u.uid} value={u.email} />)}</datalist>
          </Field></div>
          <div style={{ flex: 1 }}><Field label="Client (optional)">
            <select style={inp} value={d.projectId} onChange={(e) => { const p = (projects || []).find((x) => x.id === e.target.value); setD({ ...d, projectId: e.target.value, projectName: p ? p.name : '' }); }}>
              <option value="">—</option>{(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field></div>
        </div>
        <Field label="Notes"><textarea style={{ ...inp, resize: 'vertical' }} rows={3} value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} /></Field>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={busy || !d.title.trim()} onClick={() => onSave(d)} style={btnP}>{busy ? '…' : 'Save'}</button>
            <button onClick={onClose} style={btnG}>Cancel</button>
          </div>
          {task && task.id && <button onClick={() => onDelete(task.id)} style={{ ...btnG, color: '#b33', borderColor: '#e8cccc' }}>Delete</button>}
        </div>
      </div>
    </div>
  );
}

export function TasksView({ projects, authUser }) {
  const me = (authUser && authUser.email) || '';
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState('list');
  const [scope, setScope] = useState('mine');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cal, setCal] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const reload = () => loadTasks().then(setTasks).catch(() => {});
  useEffect(() => { reload(); loadUsers().then(setUsers).catch(() => {}); }, []);

  const visible = useMemo(() => (tasks || []).filter((t) => scope === 'all' || t.assignedTo === me || (!t.assignedTo && t.createdBy === me)), [tasks, scope, me]);

  async function save(d) {
    setBusy(true);
    try {
      await saveTask({ ...d, createdBy: d.createdBy || me, title: d.title.trim() });
      await reload(); setEditing(null);
    } catch (e) { alert('Save failed: ' + e.message); }
    setBusy(false);
  }
  async function toggleDone(t) {
    try { await saveTask({ ...t, status: t.status === 'done' ? 'open' : 'done', doneAt: t.status === 'done' ? null : fmtISO(new Date()) }); reload(); } catch (e) { alert(e.message); }
  }
  async function remove(id) {
    if (!window.confirm('Delete this item?')) return;
    try { await deleteTask(id); await reload(); setEditing(null); } catch (e) { alert(e.message); }
  }

  const todayISO = fmtISO(new Date());
  const buckets = { overdue: [], today: [], upcoming: [], nodate: [], done: [] };
  visible.forEach((t) => {
    if (t.status === 'done') buckets.done.push(t);
    else if (!t.date) buckets.nodate.push(t);
    else if (t.date < todayISO) buckets.overdue.push(t);
    else if (t.date === todayISO) buckets.today.push(t);
    else buckets.upcoming.push(t);
  });
  const bySort = (a, b) => String(a.date || '9').localeCompare(String(b.date || '9')) || String(a.time || '').localeCompare(String(b.time || ''));
  Object.values(buckets).forEach((arr) => arr.sort(bySort));

  const Item = ({ t }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderTop: '1px solid #f4f2ed' }}>
      <input type="checkbox" checked={t.status === 'done'} onChange={() => toggleDone(t)} />
      <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 10, background: meta(t.type).color + '22', color: meta(t.type).color, fontWeight: 700 }}>{meta(t.type).label}</span>
      <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setEditing(t)}>
        <div style={{ fontSize: 12, color: '#1a1a2e', textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>{t.title}</div>
        <div style={{ fontSize: 10, color: '#9a9488' }}>{t.date || 'no date'}{t.time ? ' · ' + t.time : ''}{t.projectName ? ' · ' + t.projectName : ''}{t.assignedTo && t.assignedTo !== me ? ' · → ' + t.assignedTo : ''}</div>
      </div>
      {t.date && <a href={gcalLink(t)} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3d567a', textDecoration: 'none', border: '1px solid #d2dae8', borderRadius: 5, padding: '3px 7px' }}>📅</a>}
      {t.date && <button onClick={() => downloadIcs(t)} style={{ fontSize: 11, color: '#666', border: '1px solid #e0ddd8', borderRadius: 5, padding: '3px 7px', background: '#fff', cursor: 'pointer' }}>.ics</button>}
    </div>
  );
  const Section = ({ title, items, color }) => items.length === 0 ? null : (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: color || '#9a9488', marginBottom: 4 }}>{title} ({items.length})</div>
      <div style={{ background: '#fff', border: '1px solid #eceae4', borderRadius: 8, overflow: 'hidden' }}>{items.map((t) => <Item key={t.id} t={t} />)}</div>
    </div>
  );

  // calendar grid (Mon-start)
  const cells = useMemo(() => {
    const y = cal.getFullYear(), m = cal.getMonth();
    const offset = (new Date(y, m, 1).getDay() + 6) % 7;
    const dim = new Date(y, m + 1, 0).getDate();
    const c = []; for (let i = 0; i < offset; i++) c.push(null);
    for (let d = 1; d <= dim; d++) c.push(fmtISO(new Date(y, m, d)));
    while (c.length % 7 !== 0) c.push(null);
    return c;
  }, [cal]);
  const byDate = useMemo(() => { const map = {}; visible.forEach((t) => { if (t.date) (map[t.date] = map[t.date] || []).push(t); }); return map; }, [visible]);
  const monthName = cal.toLocaleString('en', { month: 'long', year: 'numeric' });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Tasks &amp; Calendar</div>
          <div style={{ fontSize: 11, color: '#9a9488', marginTop: 2 }}>Your to-dos, meetings and reminders — assign to teammates and push to Google Calendar.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', border: '1px solid #e0ddd8', borderRadius: 6, overflow: 'hidden' }}>
            {['mine', 'all'].map((s) => <button key={s} onClick={() => setScope(s)} style={{ padding: '6px 10px', border: 'none', background: scope === s ? '#1a1a2e' : '#fff', color: scope === s ? '#c8a24e' : '#444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{s === 'mine' ? 'My items' : 'Team'}</button>)}
          </div>
          <div style={{ display: 'flex', border: '1px solid #e0ddd8', borderRadius: 6, overflow: 'hidden' }}>
            {['list', 'cal'].map((v) => <button key={v} onClick={() => setView(v)} style={{ padding: '6px 10px', border: 'none', background: view === v ? '#1a1a2e' : '#fff', color: view === v ? '#c8a24e' : '#444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{v === 'list' ? 'List' : 'Calendar'}</button>)}
          </div>
          <button onClick={() => setEditing({ date: todayISO })} style={btnP}>+ Add</button>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        {view === 'list' ? (
          <div style={{ maxWidth: 720 }}>
            <Section title="Overdue" items={buckets.overdue} color="#b33" />
            <Section title="Today" items={buckets.today} color="#b3623d" />
            <Section title="Upcoming" items={buckets.upcoming} />
            <Section title="No date" items={buckets.nodate} />
            <Section title="Done" items={buckets.done.slice(0, 30)} color="#3d7a52" />
            {visible.length === 0 && <div style={{ fontSize: 12, color: '#9a9488' }}>No items yet — click <strong>+ Add</strong>.</div>}
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <button onClick={() => setCal(new Date(cal.getFullYear(), cal.getMonth() - 1, 1))} style={btnG}>‹</button>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', minWidth: 150 }}>{monthName}</div>
              <button onClick={() => setCal(new Date(cal.getFullYear(), cal.getMonth() + 1, 1))} style={btnG}>›</button>
              <button onClick={() => { const d = new Date(); setCal(new Date(d.getFullYear(), d.getMonth(), 1)); }} style={{ ...btnG, fontWeight: 400 }}>Today</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d} style={{ fontSize: 10, color: '#9a9488', fontWeight: 700, textAlign: 'center', padding: '2px 0' }}>{d}</div>)}
              {cells.map((iso, i) => (
                <div key={i} onClick={() => iso && setEditing({ date: iso })} style={{ minHeight: 84, background: iso ? '#fff' : 'transparent', border: iso ? '1px solid #eceae4' : 'none', borderRadius: 6, padding: 4, cursor: iso ? 'pointer' : 'default', outline: iso === todayISO ? '2px solid #c8a24e' : 'none' }}>
                  {iso && <div style={{ fontSize: 10, color: iso === todayISO ? '#c8a24e' : '#9a9488', fontWeight: 700, marginBottom: 2 }}>{Number(iso.slice(8))}</div>}
                  {(byDate[iso] || []).slice(0, 4).map((t) => (
                    <div key={t.id} onClick={(e) => { e.stopPropagation(); setEditing(t); }} title={t.title} style={{ fontSize: 10, padding: '2px 4px', borderRadius: 3, marginBottom: 2, background: meta(t.type).color + '22', color: meta(t.type).color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>{t.time ? t.time + ' ' : ''}{t.title}</div>
                  ))}
                  {(byDate[iso] || []).length > 4 && <div style={{ fontSize: 9, color: '#9a9488' }}>+{byDate[iso].length - 4} more</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editing && <TaskModal task={editing} users={users} projects={projects} me={me} busy={busy} onSave={save} onClose={() => setEditing(null)} onDelete={remove} />}
    </div>
  );
}
